import type { IFilter } from "../api/models";

// PORT OF `buildQuickFilter()` IN `src/app/_pages/in-play/in-play.component.ts`.
// EACH PRESET IS GATED BY THE SAME ROLE GROUP THE ANGULAR `checkUserRoles()` USES.
export const QUICK_FILTERS = [
  { code: "my-players", label: "My Active Players", role: "PILLARS-HOST-PLAYERS-ONLY" },
  { code: "carded-players", label: "Carded Players", role: "PILLARS-HOST-CARDED-HOSTED" },
  { code: "unhosted-carded-players", label: "Unhosted Carded Players", role: "PILLARS-HOST-CARDED-UNHOSTED" },
  { code: "uncarded-players", label: "Uncarded Players", role: "PILLARS-HOST-UNCARDED" },
] as const;

export type QuickFilterCode = (typeof QUICK_FILTERS)[number]["code"];

// AN OPEN SESSION IS ONE WITH NO END TIME - EVERY PRESET STARTS HERE
const OPEN_SESSION: IFilter = {
  field: "end_time",
  value: "",
  valueDataType: "datetime",
  type: "IS",
  condition: "AND",
};

const carded = (value: "true" | "false"): IFilter => ({
  field: "is_carded",
  value,
  valueDataType: "bool",
  type: "=",
  condition: "AND",
});

// RETURNS NULL WHEN THE PRESET CANNOT BE BUILT. "MY ACTIVE PLAYERS" NEEDS THE
// SIGNED-IN USER'S HOST ROW; WITHOUT ONE THE ANGULAR PAGE NEVER STARTS POLLING,
// AND SENDING `host_id = undefined` WOULD MATCH NOTHING WHILE LOOKING LIKE "NO ONE IS PLAYING".
export function buildQuickFilter(code: QuickFilterCode, hostId: number | null): IFilter[] | null {
  switch (code) {
    case "my-players":
      if (hostId === null) return null;
      return [
        OPEN_SESSION,
        { field: "host_id", value: hostId.toFixed(), valueDataType: "number", type: "=", condition: "AND" },
        carded("true"),
      ];
    case "carded-players":
      return [OPEN_SESSION, carded("true")];
    case "unhosted-carded-players":
      return [
        OPEN_SESSION,
        { field: "host_id", value: "", valueDataType: "number", type: "IS NULL", condition: "AND" },
        carded("true"),
      ];
    case "uncarded-players":
      return [OPEN_SESSION, carded("false")];
  }
}

// PORT OF THE ROUTE-PARAM FILTER IN THE ANGULAR IN PLAY CONSTRUCTOR, APPLIED WHEN
// A MANAGER OPENS IN PLAY FROM A HOST TILE ON THE DASHBOARD. UNLIKE "MY ACTIVE
// PLAYERS" IT HAS NO `is_carded` CLAUSE - THE HOST'S OPEN SESSIONS, CARDED OR NOT.
export function buildHostFilter(hostId: number): IFilter[] {
  return [
    OPEN_SESSION,
    { field: "host_id", value: hostId.toFixed(), valueDataType: "number", type: "=", condition: "AND" },
  ];
}

// AFTER `clearFilter()`: ANGULAR SENDS ONLY THE OPEN-SESSION CLAUSE, WITH AN EMPTY
// CONDITION (`getPagedSessions` PUSHES IT WHEN THE FILTER LIST IS EMPTY)
export function buildOpenSessionsOnly(): IFilter[] {
  return [{ field: "end_time", value: "", valueDataType: "datetime", type: "IS", condition: "" }];
}

// FIRST PRESET THE USER MAY SEE AND THAT CAN ACTUALLY BE BUILT. THE ANGULAR PAGE
// ALWAYS DEFAULTS TO "MY ACTIVE PLAYERS"; FALLING THROUGH TO THE NEXT ALLOWED
// PRESET IS A PILOT CHOICE SO A USER WITHOUT A HOST ROW STILL SEES SOMETHING.
export function defaultQuickFilter(
  allowed: ReadonlySet<QuickFilterCode>,
  hostId: number | null,
): QuickFilterCode | null {
  for (const qf of QUICK_FILTERS) {
    if (allowed.has(qf.code) && buildQuickFilter(qf.code, hostId) !== null) return qf.code;
  }
  return null;
}

// PORT OF `DurationComponent.getDurationFormat` - `duration` IS IN SECONDS
export function formatDuration(seconds: number | undefined): string {
  const min = Math.floor(Math.abs(seconds ?? 0) / 60);
  const d = Math.floor(min / 1440);
  const h = Math.floor((min % 1440) / 60);
  const m = min % 60;
  if (d) return `${d}d:${h}h:${m}m`;
  if (h) return `${h}h:${m}m`;
  return `${m}m`;
}
